import React, { useEffect, useState } from "react";
import FaiBox from "../../../../../MuiComponents/FaiBox";
import FaiTypography from "../../../../../MuiComponents/FaiTypography";
import theme from "../../../../../assets/theme";
import { Form, Formik, FormikProps } from "formik";
import { Grid } from "@mui/material";
import FaiSelect from "../../../../../MuiComponents/FaiSelect";
import FormField from "../../../../../components/FormField";
import FaiButton from "../../../../../MuiComponents/FaiButton";
import * as Yup from "yup";
import {
	createSaleURL,
	getAllMasterCategoriesURL,
} from "../../../../../services/api/routes/Sale";
import { getApprovedMerchantList } from "../../../../../services/api/routes/Admin";
import Axios from "services/api/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { message } from "antd";

interface InitialObject {
	campaignName: string;
	brand: string;
	description: string;
	masterCategoryId: string;
	startDate: string;
	endDate: string;
	discountType: string;
	discount: number;
}

const DISCOUNT_TYPE_OPTIONS = [
	{ value: "PERCENTAGE", label: "Percentage" },
	{ value: "FLAT", label: "Flat Amount" },
];

// Backend Utils.parseLocalDate expects dd-MM-yyyy; <input type="date"> emits yyyy-MM-dd.
const toBackendDate = (iso: string): string => {
	if (!iso) return iso;
	const [y, m, d] = iso.split("-");
	return `${d}-${m}-${y}`;
};

const validationSchema = Yup.object().shape({
	campaignName: Yup.string()
		.required("Campaign Name is required")
		.max(40, "Campaign Name should not exceed 40 characters"),
	brand: Yup.string().required("Brand is required"),
	description: Yup.string().required("Description is required"),
	masterCategoryId: Yup.string().required("Master Category is required"),
	startDate: Yup.date().required("Start Date is required"),
	endDate: Yup.date()
		.required("End Date is required")
		.min(Yup.ref("startDate"), "End Date must be after Start Date"),
	discountType: Yup.string().required("Discount Type is required"),
	discount: Yup.number()
		.required("Discount is required")
		.min(1, "Discount must be greater than 0")
		// Only a PERCENTAGE discount is capped at 100; a FLAT amount (e.g. ₹500) has no such limit.
		.when("discountType", {
			is: "PERCENTAGE",
			then: (s) => s.max(100, "Percentage discount must be between 1 and 100"),
			otherwise: (s) => s,
		}),
});

function CreateSale({ onClose }: { onClose: () => void }) {
	const { typography, palette, borders } = theme;
	const [categories, setCategories] = useState<
		{ value: string; label: string }[]
	>([]);
	const [brands, setBrands] = useState<{ value: string; label: string }[]>([]);

	const [initialObject] = React.useState<InitialObject>({
		campaignName: "",
		brand: "",
		description: "",
		masterCategoryId: "",
		startDate: "",
		endDate: "",
		discountType: "PERCENTAGE",
		discount: 0,
	});

	// Master categories are validated against the DB by the backend, so the
	// picker must offer real ids.
	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const { data } = await Axios({ ...getAllMasterCategoriesURL });
				const list = data?.data?.masterCategories || [];
				setCategories(
					list.map((c: any) => ({ value: c.id, label: c.name }))
				);
			} catch (error) {
				console.error("Error fetching master categories:", error);
			}
		};

		// Brands live inside each merchant's brandDetails — there's no all-brands
		// endpoint, so derive the platform brand list from the approved merchants.
		// The endpoint caps page size at 100, so paginate and aggregate.
		const fetchBrands = async () => {
			try {
				const names: string[] = [];
				const size = 100; // backend max
				let page = 0;
				for (let i = 0; i < 50; i++) {
					// safety cap (~5000 merchants)
					const { data } = await Axios({
						...getApprovedMerchantList(page, size),
					});
					const resp = data?.data || {};
					const merchants = resp.data || [];
					merchants
						.flatMap((m: any) => m?.brandDetails?.brands || [])
						.map((b: any) => b?.brandName)
						.filter(Boolean)
						.forEach((n: string) => names.push(n));
					if (resp.last === true || merchants.length < size) break;
					page += 1;
				}
				const unique = Array.from(new Set(names)).sort();
				setBrands(unique.map((n) => ({ value: n, label: n })));
			} catch (error) {
				console.error("Error fetching brands:", error);
			}
		};

		fetchCategories();
		fetchBrands();
	}, []);

	const handleSubmit = async (values: InitialObject) => {
		try {
			// @ModelAttribute + MultipartFile media field => multipart/form-data.
			const formData = new FormData();
			formData.append("brand", values.brand);
			formData.append("campaignName", values.campaignName);
			formData.append("description", values.description);
			formData.append("saleType", values.discountType); // PERCENTAGE | FLAT
			formData.append("percentageFlat", String(values.discount));
			formData.append("validFromDate", toBackendDate(values.startDate));
			formData.append("validTillDate", toBackendDate(values.endDate));
			formData.append("validFromTime", "00:00");
			formData.append("validTillTime", "23:59");
			formData.append("masterCategoryIds", values.masterCategoryId);

			// Content-Type is handled by the axios interceptor: it removes the
			// default JSON header for FormData so the browser adds the multipart
			// boundary.
			const { data } = await Axios({ ...createSaleURL, data: formData });
			message.success(data?.message || "Sale created successfully");
			onClose();
		} catch (error: any) {
			console.error("Error creating sale:", error);
			message.error(
				error?.response?.data?.message ||
					error?.response?.data?.errors?.[0] ||
					"Failed to create sale. Please try again."
			);
		}
	};

	return (
		<div className="w-full h-full flex justify-center items-center">
			<FaiBox
				className="max-w-xl max-h-[90vh] overflow-y-auto my-auto"
				bgColor={palette.white.main}
				p={"15px"}
			>
				<FaiBox
					className={"flex justify-between items-center tab-title"}
					mb={"21px"}
				>
					<FaiTypography variant={"h5"} fontWeight={typography.fontWeightMedium}>
						Create sale
					</FaiTypography>
					<div className="cursor-pointer" onClick={onClose}>
						<FontAwesomeIcon icon={faClose} />
					</div>
				</FaiBox>
				<FaiBox borderRadius={borders.borderRadius.md}>
					<Formik
						initialValues={initialObject}
						enableReinitialize={true}
						validateOnBlur={true}
						validateOnChange={true}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
					>
						{(props: FormikProps<InitialObject>) => {
							const {
								setFieldValue,
								handleChange,
								handleBlur,
								values,
								touched,
								errors,
							} = props;

							return (
								<Form>
									<FaiBox mb={3}>
										<Grid container spacing={2}>
											<Grid item xs={12}>
												<FormField
													label={"Campaign Name"}
													name={"campaignName"}
													placeholder={"Enter Campaign Name"}
													value={values.campaignName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.campaignName &&
														Boolean(errors.campaignName)
													}
													helperText={
														touched.campaignName && errors.campaignName
													}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<FaiBox mb={1}>
													<FaiBox mb={1} display="inline-block">
														<FaiTypography
															component="label"
															variant="caption"
															className="!text-base !font-semibold"
														>
															Brand
														</FaiTypography>
													</FaiBox>
													<FaiSelect
														placeholder={
															brands.length ? "Choose Brand" : "No brands found"
														}
														name={"brand"}
														value={brands.find((o) => o.value === values.brand) || null}
														onChange={(opt: any) =>
															setFieldValue("brand", opt ? opt.value : "")
														}
														options={brands}
													/>
													{touched.brand && errors.brand && (
														<FaiTypography variant="caption" color="error">
															{errors.brand}
														</FaiTypography>
													)}
												</FaiBox>
											</Grid>
											<Grid item xs={12} sm={6}>
												<FaiBox mb={1}>
													<FaiBox mb={1} display="inline-block">
														<FaiTypography
															component="label"
															variant="caption"
															className="!text-base !font-semibold"
														>
															Master Category
														</FaiTypography>
													</FaiBox>
													<FaiSelect
														placeholder={"Choose Category"}
														name={"masterCategoryId"}
														value={categories.find((o) => o.value === values.masterCategoryId) || null}
														onChange={(opt: any) =>
															setFieldValue("masterCategoryId", opt ? opt.value : "")
														}
														options={categories}
													/>
												</FaiBox>
											</Grid>
											<Grid item xs={12}>
												<FormField
													label={"Description"}
													name={"description"}
													placeholder={"Enter a brief description"}
													multiline
													rows={3}
													value={values.description}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.description && Boolean(errors.description)
													}
													helperText={
														touched.description && errors.description
													}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<FormField
													type={"date"}
													label={"Start Date"}
													name={"startDate"}
													value={values.startDate}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.startDate && Boolean(errors.startDate)}
													helperText={touched.startDate && errors.startDate}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<FormField
													type={"date"}
													label={"End Date"}
													name={"endDate"}
													value={values.endDate}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.endDate && Boolean(errors.endDate)}
													helperText={touched.endDate && errors.endDate}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<FaiBox mb={1}>
													<FaiBox mb={1} display="inline-block">
														<FaiTypography
															component="label"
															variant="caption"
															className="!text-base !font-semibold"
														>
															Discount Type
														</FaiTypography>
													</FaiBox>
													<FaiSelect
														placeholder={"Choose Type"}
														name={"discountType"}
														value={DISCOUNT_TYPE_OPTIONS.find((o) => o.value === values.discountType) || null}
														onChange={(opt: any) =>
															setFieldValue("discountType", opt ? opt.value : "")
														}
														options={DISCOUNT_TYPE_OPTIONS}
													/>
												</FaiBox>
											</Grid>
											<Grid item xs={12} sm={6}>
												<FormField
													label={
														values.discountType === "FLAT"
															? "Discount Amount"
															: "Discount Percentage"
													}
													name={"discount"}
													placeholder={"Enter discount"}
													type="number"
													value={values.discount}
													onChange={(
														e: React.ChangeEvent<HTMLInputElement>
													) => {
														const value = Number(e.target.value);
														setFieldValue("discount", value < 0 ? 0 : value);
													}}
													onBlur={handleBlur}
													error={touched.discount && Boolean(errors.discount)}
													helperText={touched.discount && errors.discount}
												/>
											</Grid>
										</Grid>
									</FaiBox>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={4}>
											<FaiButton
												color="primary"
												variant={"contained"}
												fullWidth
												type="submit"
											>
												Add Sale
											</FaiButton>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FaiButton
												color="text"
												variant={"outlined"}
												fullWidth
												onClick={onClose}
											>
												Cancel
											</FaiButton>
										</Grid>
									</Grid>
								</Form>
							);
						}}
					</Formik>
				</FaiBox>
			</FaiBox>
		</div>
	);
}

export default CreateSale;
